/**
 * Copyright 2025 John Huikku Alienrobot LLC
 * www.ALIENROBOT.com
 */

import { useState, useRef, useEffect } from 'react'
import { Save, FileText, X, Maximize2, Minimize2 } from 'lucide-react'
import ReactMarkdown from 'react-markdown'

interface BrainstormDocumentEditorProps {
  onClose: () => void
  onSave: (content: string, filePath?: string) => void
  initialContent?: string
  fileName?: string
  onFileLoaded?: (filePath: string, content: string) => void // Callback when file is loaded via drag-drop
}

export function BrainstormDocumentEditor({
  onClose,
  onSave,
  initialContent = '',
  fileName = 'Untitled Document',
  onFileLoaded
}: BrainstormDocumentEditorProps) {
  const [content, setContent] = useState(initialContent)
  const [isPreview, setIsPreview] = useState(false)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const [currentFilePath, setCurrentFilePath] = useState<string | null>(null)
  const [isDraggingOver, setIsDraggingOver] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    setHasUnsavedChanges(content !== initialContent)
  }, [content, initialContent])

  const handleSave = () => {
    onSave(content, currentFilePath || undefined)
    setHasUnsavedChanges(false)
  }

  const handleClose = () => {
    if (hasUnsavedChanges) {
      if (confirm('You have unsaved changes. Are you sure you want to close?')) {
        onClose()
      }
    } else {
      onClose()
    }
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDraggingOver(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDraggingOver(false)
  }

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDraggingOver(false)

    console.log('[Document Editor] Drop event received')
    console.log('[Document Editor] DataTransfer types:', e.dataTransfer.types)

    try {
      // First, check for file data from global variable (set during drag start from file list)
      let draggedData = (window as any).__draggedFileData
      console.log('[Document Editor] window.__draggedFileData:', draggedData)

      // If not available yet, try to get file paths and load them ourselves
      if (!draggedData) {
        const filePathsJson = e.dataTransfer.getData('application/x-hivemind-file-paths')
        console.log('[Document Editor] File paths JSON:', filePathsJson)

        if (filePathsJson) {
          console.log('[Document Editor] Loading file from drag data...')
          const filePaths = JSON.parse(filePathsJson) as string[]
          console.log('[Document Editor] Parsed file paths:', filePaths)

          if (filePaths.length > 0) {
            // Import necessary functions
            const { getDirectoryHandle } = await import('@/lib/storage')
            const { readFile } = await import('@/lib/filesystem')
            const { useProjectStore } = await import('@/ui/stores/project-store')
            const currentProject = useProjectStore.getState().currentProject

            console.log('[Document Editor] Current project:', currentProject?.name, currentProject?.id)

            if (currentProject?.id) {
              const dirHandle = await getDirectoryHandle(currentProject.id)
              console.log('[Document Editor] Directory handle:', dirHandle)

              if (dirHandle) {
                const filePath = filePaths[0] // Take first file
                console.log('[Document Editor] Reading file:', filePath)
                const content = await readFile(dirHandle, filePath)
                console.log('[Document Editor] File content length:', content?.length)

                if (content !== null) {
                  draggedData = [{ filePath, content }]
                }
              }
            }
          }
        }
      }

      if (draggedData) {
        // Clear the global variable
        delete (window as any).__draggedFileData

        // Handle multiple files (take the first one)
        if (Array.isArray(draggedData) && draggedData.length > 0) {
          const file = draggedData[0]
          console.log('[Document Editor] 📄 Loading file:', file.filePath)
          setContent(file.content)
          setCurrentFilePath(file.filePath)
          setHasUnsavedChanges(false)

          // Notify parent that a file was loaded
          if (onFileLoaded) {
            onFileLoaded(file.filePath, file.content)
          }
        } else if (!Array.isArray(draggedData)) {
          // Single file (legacy support)
          const { filePath, content: fileContent } = draggedData
          console.log('[Document Editor] 📄 Loading file:', filePath)
          setContent(fileContent)
          setCurrentFilePath(filePath)
          setHasUnsavedChanges(false)

          // Notify parent that a file was loaded
          if (onFileLoaded) {
            onFileLoaded(filePath, fileContent)
          }
        }
      } else {
        console.log('[Document Editor] No file data found in drop event')
      }
    } catch (error) {
      console.error('[Document Editor] Failed to handle drop:', error)
    }
  }

  return (
    <div
      className={`flex flex-col h-full bg-background border-l border-border ${isFullscreen ? 'fixed inset-0 z-50' : ''} ${isDraggingOver ? 'ring-2 ring-accent' : ''}`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* Header */}
      <div className="flex items-center justify-between p-3 border-b border-border bg-background-secondary">
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <FileText className="w-4 h-4 text-accent" />
            <span className="text-sm font-medium text-foreground">{currentFilePath || fileName}</span>
            {hasUnsavedChanges && <span className="text-xs text-yellow-500">●</span>}
          </div>
          {currentFilePath && (
            <span className="text-xs text-foreground-muted ml-6">Editing: {currentFilePath}</span>
          )}
        </div>
        
        <div className="flex items-center gap-2">
          <button
            onClick={() => setIsPreview(!isPreview)}
            className="btn-secondary text-xs px-2 py-1"
          >
            {isPreview ? 'Edit' : 'Preview'}
          </button>
          
          <button
            onClick={() => setIsFullscreen(!isFullscreen)}
            className="btn-secondary p-1"
            title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
          >
            {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
          </button>
          
          <button
            onClick={handleSave}
            className="btn-primary text-xs px-3 py-1 flex items-center gap-1"
            disabled={!hasUnsavedChanges}
          >
            <Save className="w-3 h-3" />
            Save
          </button>
          
          <button
            onClick={handleClose}
            className="btn-secondary p-1"
            title="Close editor"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Editor/Preview */}
      <div className="flex-1 overflow-hidden relative">
        {isDraggingOver && (
          <div className="absolute inset-0 bg-accent/10 border-2 border-dashed border-accent z-10 flex items-center justify-center pointer-events-none">
            <div className="bg-background-secondary px-4 py-2 rounded-lg border border-accent">
              <p className="text-accent font-medium">Drop file here to edit</p>
            </div>
          </div>
        )}

        {isPreview ? (
          <div className="h-full overflow-y-auto p-4 prose prose-invert max-w-none">
            <ReactMarkdown>{content}</ReactMarkdown>
          </div>
        ) : (
          <textarea
            ref={textareaRef}
            value={content}
            onChange={(e) => setContent(e.target.value)}
            className="w-full h-full p-4 bg-background text-foreground font-mono text-sm resize-none focus:outline-none"
            placeholder="Start writing your document here...

You can use Markdown formatting:
# Heading 1
## Heading 2
**bold** *italic*
- List item
1. Numbered item
[link](url)

Or drag a file from the file list to edit it here.
"
          />
        )}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between p-2 border-t border-border bg-background-secondary text-xs text-foreground-muted">
        <span>{content.length} characters</span>
        <span>{content.split('\n').length} lines</span>
      </div>
    </div>
  )
}

